import { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CoverThumbnail, type TextZoneOverride, type ExtraTextZone } from '@/components/CoverThumbnail';
import type { Cover } from '@/lib/api';

interface CoverPreviewDialogProps {
  cover: Cover;
  destination: string;
  year: string;
  onClose: () => void;
  /**
   * The client's view: cover, and a confirm button. Nothing else.
   *
   * ⚠️ THE TITLE IS NOT LOST, IT IS ALREADY KNOWN. `destination` and `year`
   * come from the order, which the assistant filled from the WhatsApp
   * conversation ("Mariage"), and both are still passed to `onConfirm`
   * unchanged. What is removed is asking the client to type them a second
   * time — owner's decision, 2026-08-16: "the client will probably tell you
   * what to put in the title on the chat conversation."
   *
   * Off by default, so the team's own editor keeps every tool.
   */
  simple?: boolean;
  onConfirm: (data: {
    destinationText: string;
    yearText: string;
    zoneOverrides: Record<number, TextZoneOverride>;
    extraZones: ExtraTextZone[];
    customTexts: Record<number, string>;
  }) => void;
}

export function CoverPreviewDialog({
  cover,
  destination: _initialDest,
  year: _initialYear,
  onClose,
  simple = false,
  onConfirm,
}: CoverPreviewDialogProps) {
  const [destText, setDestText] = useState(_initialDest || '');
  const [yearText, setYearText] = useState(_initialYear || '');
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [zoneOverrides, setZoneOverrides] = useState<Record<number, TextZoneOverride>>({});
  const [customTexts, setCustomTexts] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    (cover.text_zones ?? []).forEach((z, i) => {
      if (z.type === 'custom') init[i] = z.default_text ?? '';
    });
    return init;
  });
  const [extraZones, setExtraZones] = useState<ExtraTextZone[]>([]);

  const zones = cover.text_zones ?? [];

  // Get the cover's dominant font and size for new extra zones
  const coverFonts = [...new Set(zones.map((z) => z.font_family).filter(Boolean))];
  const coverDefaultFont = coverFonts[0] ?? 'Bungee';
  const coverDefaultSize = zones[0]?.font_size ?? 48;
  const coverDefaultColor = zones[0]?.color ?? '#ffffff';

  const getZoneLabel = (zoneIndex: number) => {
    const z = zones[zoneIndex];
    if (!z) return `Zone ${zoneIndex + 1}`;
    if (z.type === 'destination') return 'Titre de l’album';
    if (z.type === 'year') return 'Année';
    return 'Sous-titre';
  };

  const handleZoneChange = useCallback(
    (zoneIndex: number, override: TextZoneOverride) => {
      setZoneOverrides((prev) => ({ ...prev, [zoneIndex]: { ...prev[zoneIndex], ...override } }));
    }, []
  );

  const handleExtraZoneChange = useCallback(
    (extraIndex: number, updated: ExtraTextZone) => {
      setExtraZones((prev) => prev.map((ez, i) => (i === extraIndex ? updated : ez)));
    }, []
  );

  // Hide (delete) a template zone — hides it from preview
  const handleHideZone = useCallback(
    (zoneIndex: number) => {
      setZoneOverrides((prev) => ({ ...prev, [zoneIndex]: { ...prev[zoneIndex], hidden: true } }));
      if (selectedZone === String(zoneIndex)) setSelectedZone(null);
    }, [selectedZone]
  );

  const handleUnhideZone = useCallback(
    (zoneIndex: number) => {
      setZoneOverrides((prev) => {
        const next = { ...prev };
        if (next[zoneIndex]) next[zoneIndex] = { ...next[zoneIndex], hidden: false };
        return next;
      });
    }, []
  );

  const handleDeleteExtra = useCallback(
    (extraIndex: number) => {
      setExtraZones((prev) => prev.filter((_, i) => i !== extraIndex));
      if (selectedZone === `extra-${extraIndex}`) setSelectedZone(null);
    }, [selectedZone]
  );

  // Add new text — inherits cover's font, size, and color
  const handleAddText = useCallback(() => {
    setExtraZones((prev) => [
      ...prev,
      {
        text: 'Texte',
        x: 0.5,
        y: 0.45 + prev.length * 0.1,
        width: 0.8,
        height: 0.1,
        fontSize: coverDefaultSize,
        fontFamily: coverDefaultFont,
        color: coverDefaultColor,
        align: 'center',
      },
    ]);
  }, [coverDefaultSize, coverDefaultFont, coverDefaultColor]);

  const handleResetZone = useCallback(() => {
    if (selectedZone === null || selectedZone.startsWith('extra-')) return;
    const idx = parseInt(selectedZone, 10);
    setZoneOverrides((prev) => { const next = { ...prev }; delete next[idx]; return next; });
  }, [selectedZone]);

  // Selected zone info
  const selectedIsTemplate = selectedZone !== null && !selectedZone.startsWith('extra-');
  const selectedIsExtra = selectedZone !== null && selectedZone.startsWith('extra-');
  const selectedTemplateIdx = selectedIsTemplate ? parseInt(selectedZone!, 10) : null;
  const selectedExtraIdx = selectedIsExtra ? parseInt(selectedZone!.replace('extra-', ''), 10) : null;
  const selectedOverride = selectedTemplateIdx !== null ? zoneOverrides[selectedTemplateIdx] : undefined;
  const selectedScale = selectedOverride?.scale ?? 1;
  const selectedExtraZone = selectedExtraIdx !== null ? extraZones[selectedExtraIdx] : undefined;

  const hiddenZones = zones
    .map((z, i) => ({ zone: z, index: i }))
    .filter(({ index }) => zoneOverrides[index]?.hidden);

  const handleCancel = useCallback(() => {
    setDestText(''); setYearText(''); setSelectedZone(null);
    setZoneOverrides({}); setCustomTexts({}); setExtraZones([]);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={true} onClose={handleCancel} size="xl" title={cover.name}>
      <div className={`p-6 gap-6 ${simple ? '' : 'grid md:grid-cols-[1fr_280px]'}`}>
        {/* Cover preview */}
        <div>
          <p className="text-xs text-gray-500 mb-2">Aperçu de votre couverture</p>
          <CoverThumbnail
            cover={cover}
            destinationText={destText}
            yearText={yearText}
            customTexts={customTexts}
            zoneOverrides={zoneOverrides}
            extraZones={extraZones}
            interactive={!simple}
            selectedZone={selectedZone}
            onZoneSelect={setSelectedZone}
            onZoneChange={handleZoneChange}
            onZoneDelete={handleHideZone}
            onExtraZoneChange={handleExtraZoneChange}
            onExtraZoneDelete={handleDeleteExtra}
            className="rounded-xl border border-gray-200 shadow-lg"
          />
          {!simple && (
            <p className="text-xs text-gray-400 mt-2 text-center">
              Cliquez sur un texte — glissez pour déplacer, coins pour redimensionner
            </p>
          )}
        </div>

        {/* Edit fields + tools — the team's editor only. */}
        {!simple && (
        <div className="space-y-4 overflow-y-auto max-h-[70vh]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre de l’album</label>
            <input
              type="text" value={destText} onChange={(e) => setDestText(e.target.value)}
              maxLength={50}
              placeholder="Ex: Bali"
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 overflow-hidden text-ellipsis"
            />
            <p className="text-xs text-gray-400 mt-1">Remplace le texte principal sur la couverture</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Année</label>
            <input
              type="text" value={yearText} onChange={(e) => setYearText(e.target.value)}
              maxLength={50}
              placeholder="Ex: 2024"
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 overflow-hidden text-ellipsis"
            />
          </div>

          {/* Custom zones */}
          {zones.map((zone, i) => {
            if (zone.type !== 'custom') return null;
            const isHidden = zoneOverrides[i]?.hidden;
            return (
              <div key={`custom-${i}`} className={isHidden ? 'opacity-40' : ''}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">Sous-titre</label>
                  <button
                    onClick={() => (isHidden ? handleUnhideZone(i) : handleHideZone(i))}
                    className={`text-xs px-2 py-0.5 rounded ${isHidden ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-red-50 text-red-500 hover:bg-red-100'} transition-colors`}
                  >
                    {isHidden ? 'Afficher' : 'Masquer'}
                  </button>
                </div>
                <input
                  type="text" value={customTexts[i] ?? ''}
                  onChange={(e) => setCustomTexts((prev) => ({ ...prev, [i]: e.target.value }))}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={zone.default_text ?? 'Texte personnalisé'}
                  disabled={isHidden}
                />
                <p className="text-xs text-gray-400 mt-1">Police: {zone.font_family ?? 'par défaut'}</p>
              </div>
            );
          })}

          {/* Extra text zones */}
          {extraZones.map((ez, i) => (
            <div key={`extra-input-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Texte libre{extraZones.length > 1 ? ` ${i + 1}` : ''}
                </label>
                <button onClick={() => handleDeleteExtra(i)}
                  className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                  Supprimer
                </button>
              </div>
              <input
                type="text" value={ez.text}
                onChange={(e) => setExtraZones((prev) => prev.map((z, j) => (j === i ? { ...z, text: e.target.value } : z)))}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="Votre texte..."
              />
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-gray-400">Couleur:</span>
                <input type="color" value={ez.color}
                  onChange={(e) => setExtraZones((prev) => prev.map((z, j) => (j === i ? { ...z, color: e.target.value } : z)))}
                  className="w-6 h-6 rounded border border-gray-200 cursor-pointer" />
                <span className="text-xs text-gray-400 ml-auto">
                  Police: {ez.fontFamily}
                </span>
              </div>
            </div>
          ))}

          <button onClick={handleAddText}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-brand-500 hover:text-brand-500 transition-colors">
            + Ajouter un texte
          </button>

          {/* Hidden zones recovery */}
          {hiddenZones.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-2.5 space-y-1.5">
              <p className="text-xs font-medium text-gray-500">Textes masqués:</p>
              {hiddenZones.map(({ zone, index }) => (
                <button key={index} onClick={() => handleUnhideZone(index)}
                  className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 w-full">
                  <span className="w-3 h-3 rounded border border-gray-300 flex items-center justify-center text-[8px]">+</span>
                  <span className="truncate">{getZoneLabel(index)}: {zone.default_text ?? '...'}</span>
                </button>
              ))}
            </div>
          )}

          {/* Scale/size tools for selected template zone */}
          {selectedIsTemplate && selectedTemplateIdx !== null && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-blue-800">{getZoneLabel(selectedTemplateIdx)}</p>
                <button onClick={() => setSelectedZone(null)} className="text-blue-400 hover:text-blue-600 text-xs">✕</button>
              </div>
              <div>
                <label className="flex items-center justify-between text-xs text-blue-700 mb-1">
                  <span>Taille</span>
                  <span className="font-mono">{Math.round(selectedScale * 100)}%</span>
                </label>
                <input type="range" min="20" max="500" value={Math.round(selectedScale * 100)}
                  onChange={(e) => handleZoneChange(selectedTemplateIdx, { ...zoneOverrides[selectedTemplateIdx], scale: parseInt(e.target.value, 10) / 100 })}
                  className="w-full h-1.5 bg-blue-200 rounded-full appearance-none cursor-pointer accent-blue-500" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleResetZone}
                  className="flex-1 text-xs bg-white border border-blue-200 text-blue-700 px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors">
                  Réinitialiser
                </button>
                <button onClick={() => handleZoneChange(selectedTemplateIdx, { scale: 1, x: undefined, y: undefined })}
                  className="flex-1 text-xs bg-white border border-blue-200 text-blue-700 px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors">
                  Zoom fit
                </button>
              </div>
            </div>
          )}

          {/* Size tool for selected extra zone */}
          {selectedIsExtra && selectedExtraIdx !== null && selectedExtraZone && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-emerald-800">Texte libre{extraZones.length > 1 ? ` ${selectedExtraIdx + 1}` : ''}</p>
                <button onClick={() => setSelectedZone(null)} className="text-emerald-400 hover:text-emerald-600 text-xs">✕</button>
              </div>
              <div>
                <label className="flex items-center justify-between text-xs text-emerald-700 mb-1">
                  <span>Taille</span>
                  <span className="font-mono">{Math.round(selectedExtraZone.fontSize)}pt</span>
                </label>
                <input type="range" min="12" max="200" value={Math.round(selectedExtraZone.fontSize)}
                  onChange={(e) => setExtraZones((prev) => prev.map((z, j) => (j === selectedExtraIdx ? { ...z, fontSize: parseInt(e.target.value, 10) } : z)))}
                  className="w-full h-1.5 bg-emerald-200 rounded-full appearance-none cursor-pointer accent-emerald-500" />
              </div>
            </div>
          )}

          {/* Font info */}
          {zones.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-gray-700">Polices utilisées:</p>
              {coverFonts.map((font, i) => (
                <p key={i} className="text-xs font-semibold text-gray-600" style={{ fontFamily: font ?? undefined }}>{font}</p>
              ))}
            </div>
          )}

          <div className="pt-2 text-xs text-gray-500 space-y-1">
            <p>- Polices Google Fonts (même rendu à l'impression)</p>
            <p>- Le dos et la tranche sont générés automatiquement</p>
          </div>
        </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
        <Button variant="secondary" onClick={handleCancel}>Annuler</Button>
        <Button onClick={() => onConfirm({
          destinationText: destText,
          yearText,
          zoneOverrides,
          extraZones,
          customTexts,
        })}>{simple ? 'Confirmer cette couverture' : 'Choisir cette couverture'}</Button>
      </div>
    </Modal>
  );
}
